import { useEffect, useMemo, useRef } from 'react';
import isEqual from 'hs-lodash/isEqual';
import { FEATURES } from 'collaboration-sidebar/constants/features';
import usePrevious from 'collaboration-sidebar/hooks/usePrevious';
import { hasFeature } from '../utils/features';
import { mergeCollaborationSidebarState } from '../utils';
import useGetPropsFromFAS from '../features/fas/hooks/useGetPropsFromFAS';

/**
 * Today, several prop values (such as objectId ,objectType, ctaUrl)
 * are duplicated in CollaborationSidebarState, where they can be read
 * by the mini commenter. To ensure these values are up to date, we
 * sync into the state from props.
 */
export default function usePersistPropsToCollaborationSidebarState({
  collaborationSidebarState,
  onUpdateCollaborationSidebarState,
  collaborationSidebarProps: {
    objectId,
    objectType,
    appName,
    enabledFeatures,
    ctaUrl,
    tasksConfig
  }
}) {
  'use no memo';

  const {
    fasProps
  } = useGetPropsFromFAS({
    appName: appName || '',
    enabledFeatures
  });
  const isEmbeddedCommentingEnabled = hasFeature(fasProps === null || fasProps === void 0 ? void 0 : fasProps.enabledFeatures, FEATURES.DEEP_COMMENTING);
  const currentUrl = window.location.href;
  const updatesToPersist = useMemo(() => ({
    isEmbeddedCommentingEnabled,
    appName,
    objectId,
    objectType: (fasProps === null || fasProps === void 0 ? void 0 : fasProps.objectType) || objectType,
    ctaUrl: ctaUrl || currentUrl,
    enabledFeatures: (fasProps === null || fasProps === void 0 ? void 0 : fasProps.enabledFeatures) || [],
    tasksConfig: tasksConfig || {}
  }), [appName, ctaUrl, currentUrl, fasProps === null || fasProps === void 0 ? void 0 : fasProps.enabledFeatures, fasProps === null || fasProps === void 0 ? void 0 : fasProps.objectType, isEmbeddedCommentingEnabled, objectId, objectType, tasksConfig]);
  const prevUpdatesToPersist = usePrevious(updatesToPersist);
  const mounted = useRef(false);

  /**
   * Persist prop updates to state
   */
  useEffect(() => {
    if (!mounted.current || !isEqual(prevUpdatesToPersist, updatesToPersist)) {
      onUpdateCollaborationSidebarState(updatesToPersist);
    }
    mounted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevUpdatesToPersist, updatesToPersist]);

  /**
   * As an optimization, return updated version (avoid unnecessary prop updates in iframe)
   */
  return useMemo(() => mergeCollaborationSidebarState(collaborationSidebarState, updatesToPersist), [collaborationSidebarState, updatesToPersist]);
}